/**
 * OnboardingBackdrop — затемняет всё, кроме области якоря.
 *
 * JB 2026-05-26: «онбординг должен ставить паузу фокусировать внимание
 * игрока». Раньше bubble просто всплывал поверх UI и игра продолжалась —
 * можно было тапать Искру, копить осознания, кристаллизовать, пока tip
 * висит. Теперь backdrop:
 *
 *   1. Перекрывает весь viewport полупрозрачной графитовой плёнкой.
 *   2. Прорезает в ней «окно» вокруг якоря через clip-path с evenodd
 *      fill rule — тёмная область с дыркой для focus-area.
 *   3. Перехватывает клики ВНЕ окна (pointer-events:auto на затемнении)
 *      — никакие тапы не доходят до Spark, апгрейдов, settings и т.д.
 *   4. Внутри окна — pointer-events наследуется от того, что лежит
 *      под backdrop'ом: tap на Искру/кнопку проходит через дырку.
 *
 * Окно вычисляется по anchor rect (тот же `getAnchorRect` что у focus
 * ring и tip bubble). top-banner — без окна, весь backdrop затемнён.
 * Bubble + close button лежат на z-index ВЫШЕ backdrop'а — они тоже
 * остаются интерактивными.
 *
 * Окно поллится каждые 250ms (как и остальные onboarding-визуалы) чтобы
 * следовать за движущимся якорем (cooldown ring, появление кнопок и т.п.).
 */
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { getAnchorRect } from './anchors.js';
import { useOnboardingStore } from './store.js';
import type { TipAnchor } from './types.js';
import styles from './OnboardingBackdrop.module.css';

/** Запас вокруг якоря — чтобы окно было ощутимо больше элемента. */
const HOLE_PADDING = 16;
/** Радиус скругления окна — мягкий oval, не острый прямоугольник. */
const HOLE_RADIUS = 22;

interface Hole {
  readonly top: number;
  readonly left: number;
  readonly width: number;
  readonly height: number;
}

function holeFor(anchor: TipAnchor): Hole | null {
  if (anchor === 'top-banner') return null;
  const rect = getAnchorRect(anchor);
  if (rect === null) return null;
  return {
    top: rect.top - HOLE_PADDING,
    left: rect.left - HOLE_PADDING,
    width: rect.width + HOLE_PADDING * 2,
    height: rect.height + HOLE_PADDING * 2,
  };
}

export function OnboardingBackdrop(): JSX.Element | null {
  const tip = useOnboardingStore((s) => s.current);
  const [hole, setHole] = useState<Hole | null>(null);

  useEffect(() => {
    if (tip === null) {
      setHole(null);
      return;
    }
    const refresh = (): void => {
      setHole(holeFor(tip.anchor));
    };
    refresh();
    const id = window.setInterval(refresh, 250);
    return () => {
      window.clearInterval(id);
    };
  }, [tip]);

  if (tip === null) return null;

  // Если у якоря нет rect (top-banner или anchor ещё не зарегистрирован)
  // — рендерим полный backdrop без дырки.
  if (hole === null) {
    return (
      <motion.div
        className={styles.fullBackdrop}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        aria-hidden
      />
    );
  }

  // CSS clip-path: чёткий outer (viewport) с вырезом (hole) через evenodd.
  // SVG mask был бы чище но clip-path работает на mobile без дополнительных
  // элементов — лёгкий путь.
  // Используем CSS polygon hack: рисуем 8 прямоугольников вокруг hole через
  // div boxes — каждый накрывает свою четверть viewport'а минус область
  // hole. Проще и работает везде.
  return (
    <>
      {/* Верхняя полоса */}
      <motion.div
        className={styles.shade}
        style={{ top: 0, left: 0, right: 0, height: `${hole.top.toString()}px` }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        aria-hidden
      />
      {/* Нижняя полоса */}
      <motion.div
        className={styles.shade}
        style={{
          top: `${(hole.top + hole.height).toString()}px`,
          left: 0,
          right: 0,
          bottom: 0,
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        aria-hidden
      />
      {/* Левая полоса (между верхней и нижней) */}
      <motion.div
        className={styles.shade}
        style={{
          top: `${hole.top.toString()}px`,
          left: 0,
          width: `${hole.left.toString()}px`,
          height: `${hole.height.toString()}px`,
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        aria-hidden
      />
      {/* Правая полоса */}
      <motion.div
        className={styles.shade}
        style={{
          top: `${hole.top.toString()}px`,
          left: `${(hole.left + hole.width).toString()}px`,
          right: 0,
          height: `${hole.height.toString()}px`,
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        aria-hidden
      />
      {/* Мягкий outline вокруг окна — не строго прямоугольный, чтоб
          граница не бросалась в глаза, а просто намекала на focus area. */}
      <motion.div
        className={styles.holeOutline}
        style={{
          top: `${hole.top.toString()}px`,
          left: `${hole.left.toString()}px`,
          width: `${hole.width.toString()}px`,
          height: `${hole.height.toString()}px`,
          borderRadius: `${HOLE_RADIUS.toString()}px`,
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.45, ease: 'easeOut' }}
        aria-hidden
      />
    </>
  );
}
